import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LanguageIcon from "@mui/icons-material/Language";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useTranslation } from "react-i18next";
import { useBrand } from "../brand/BrandContext";
import { logoutUser } from "../api/users";
import {
  getDecodedToken,
  getUserEmail,
  userHasGroup,
} from "../utils/auth";
import {
  getCabinetNavItems,
  getCabinetRoleLabelKey,
  isNavItemActive,
} from "./adminNav";

const DRAWER_WIDTH = 256;

function brandInitials(name) {
  const compact = String(name || "").replace(/\s+/g, "");
  return (compact.slice(0, 2) || "A").toUpperCase();
}

function userInitials(token, email) {
  const first = (token?.first_name || "").trim();
  const last = (token?.last_name || "").trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || email?.slice(0, 2).toUpperCase();
  }
  return String(email || "?").slice(0, 2).toUpperCase();
}

export default function AdminLayout() {
  const { t, i18n } = useTranslation("common");
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [accountEl, setAccountEl] = useState(null);
  const searchRef = useRef(null);

  const email = getUserEmail();
  const token = getDecodedToken();
  const navItems = getCabinetNavItems();
  const showSearch = userHasGroup("Company Admin");
  const tenantName = brand?.tenant?.displayName || brand?.name || "";

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submitSearch = (event) => {
    event?.preventDefault?.();
    const q = search.trim();
    if (!q || !showSearch) return;
    navigate(`/administrator/bookings?search=${encodeURIComponent(q)}`);
  };

  const accountMenu = (
    <Menu
      anchorEl={accountEl}
      open={Boolean(accountEl)}
      onClose={() => setAccountEl(null)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <MenuItem
        onClick={() => {
          setAccountEl(null);
          navigate("/profile");
        }}
      >
        <ListItemIcon>
          <AccountCircleIcon fontSize="small" />
        </ListItemIcon>
        {t("header.myAccount")}
      </MenuItem>
      <MenuItem
        onClick={() => {
          setAccountEl(null);
          navigate("/");
        }}
      >
        <ListItemIcon>
          <LanguageIcon fontSize="small" />
        </ListItemIcon>
        {t("admin.toWebsite")}
      </MenuItem>
      <Divider />
      <MenuItem
        onClick={() => {
          setAccountEl(null);
          logoutUser();
        }}
      >
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        {t("header.logout")}
      </MenuItem>
    </Menu>
  );

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          height: 64,
          px: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          {brandInitials(tenantName)}
        </Box>
        <Box minWidth={0}>
          <Typography variant="subtitle2" noWrap fontWeight={600}>
            {tenantName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {t(getCabinetRoleLabelKey())}
          </Typography>
        </Box>
      </Box>

      <List sx={{ px: 1, py: 1.5, flexGrow: 1 }}>
        {navItems.map((item, index) => {
          if (item.divider) {
            return <Divider key={item.key || `div-${index}`} sx={{ my: 1.25, mx: 1.5 }} />;
          }
          const Icon = item.icon;
          const active = isNavItemActive(item, location.pathname, location.search);
          return (
            <ListItemButton
              key={item.to + item.labelKey}
              component={RouterLink}
              to={item.to}
              selected={active}
              sx={{
                mb: 0.25,
                borderRadius: 1,
                minHeight: 44,
                "&.Mui-selected": {
                  bgcolor: "action.selected",
                  "&:hover": { bgcolor: "action.selected" },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: active ? "primary.main" : "text.secondary" }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t(item.labelKey)}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
        <ListItemButton
          onClick={(event) => setAccountEl(event.currentTarget)}
          sx={{ borderRadius: 1, minHeight: 48 }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              mr: 1.25,
              fontSize: 13,
              bgcolor: "primary.main",
            }}
          >
            {userInitials(token, email)}
          </Avatar>
          <Box minWidth={0} flex={1}>
            <Typography variant="body2" noWrap fontWeight={500}>
              {email || t("header.account")}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {t(getCabinetRoleLabelKey())}
            </Typography>
          </Box>
          <UnfoldMoreIcon fontSize="small" sx={{ color: "text.disabled" }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ gap: { xs: 1, md: 2 }, minHeight: 64, px: { xs: 1, sm: 2 } }}>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: "none" } }}
            aria-label={t("header.openMenu")}
          >
            <MenuIcon />
          </IconButton>
          {showSearch ? (
            <Box
              component="form"
              onSubmit={submitSearch}
              sx={{ flex: 1, maxWidth: 460 }}
            >
              <TextField
                inputRef={searchRef}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("admin.searchBookingsPlaceholder")}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end" sx={{ display: { xs: "none", md: "flex" } }}>
                      <Typography
                        variant="caption"
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 0.5,
                          px: 0.75,
                          py: 0.25,
                          color: "text.disabled",
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}
                      >
                        ⌘K
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.default",
                  },
                }}
              />
            </Box>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          <ToggleButtonGroup
            value={i18n.language}
            exclusive
            size="small"
            onChange={(_, value) => value && i18n.changeLanguage(value)}
          >
            <ToggleButton value="uk" aria-label={t("language.uk")}>
              UA
            </ToggleButton>
            <ToggleButton value="en" aria-label={t("language.en")}>
              EN
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            onClick={(event) => setAccountEl(event.currentTarget)}
            aria-label={t("header.account")}
          >
            <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: "primary.main" }}>
              {userInitials(token, email)}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              borderRight: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0,
          pt: "64px",
        }}
      >
        <Outlet />
      </Box>
      {accountMenu}
    </Box>
  );
}
